import React, { useState, useEffect, useMemo } from 'react';
import { X, Upload, Check, AlertCircle, Loader2, FileSpreadsheet } from 'lucide-react';
import readXlsxFile from 'read-excel-file/web-worker';
import { supabase } from '../../../lib/supabaseClient';
import { invokeAdminManageUser, getValidSessionAccessToken } from '../../../lib/adminManageUserClient';
import { downloadUserTemplate } from '../../../lib/utils';
import ScheduledDateTimePicker from '../../../components/ScheduledDateTimePicker.jsx';
import {
  INVITE_START_PLACEHOLDER,
  buildInviteStartAtIso,
  getSaoPauloNowDate,
  parseInviteStartInput,
} from '../../../lib/saoPauloDateTime.js';

const REQUEST_TIMEOUT_MS = 45000;
const ALLOWED_ROLES = ['Aluno', 'Instrutor', 'Coordenador', 'Ponto Focal', 'Parceiro SAS', 'Gestor Parceiro', 'Admin'];
const FIELD_LABELS = {
  name: 'Nome Completo',
  email: 'E-mail',
  company: 'Empresa',
  cpf: 'CPF',
  partner: 'Parceiro SAS',
  profile: 'Perfil',
  whatsapp: 'Celular',
  cargo: 'Cargo',
  area: 'Área'
};
const REQUIRED_FIELDS = ['name', 'email'];
const INVITE_START_MODES = {
  immediate: 'immediate',
  scheduled: 'scheduled',
};
const HEADER_ALIASES = {
  name: ['nome completo', 'nome'],
  email: ['e mail', 'email'],
  company: ['empresa', 'empresa opcional', 'empresa (opcional)'],
  cpf: ['cpf', 'cpf 000 000 000 00', 'cpf opcional', 'cpf opcional 000 000 000 00', 'cpf (opcional)', 'cpf opcional (000 000 000 00)'],
  partner: ['parceiro sas', 'parceiro', 'parceiro sas opcional', 'parceiro sas (opcional)'],
  profile: ['perfil', 'perfil opcional', 'perfil (opcional)'],
  whatsapp: ['celular', 'celular 00 00000 0000', 'whatsapp', 'whatsapp 00 00000 0000'],
  cargo: ['cargo', 'cargo opcional', 'cargo (opcional)'],
  area: ['area', 'area opcional', 'area (opcional)']
};

const BulkCreateUserModal = ({ isOpen, onClose, onSuccess, partners, companies }) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [logs, setLogs] = useState([]);
  const [completionSummary, setCompletionSummary] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [inviteStartMode, setInviteStartMode] = useState('');
  const [inviteStartDate, setInviteStartDate] = useState('');
  const [autoEnroll, setAutoEnroll] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [showOnlySelectedCourses, setShowOnlySelectedCourses] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [validityDays, setValidityDays] = useState(365);
  
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  const toggleCourseSelection = (courseId) => {
    setSelectedCourses((prev) => (
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    ));
  };

  const selectAllVisibleCourses = () => {
    setSelectedCourses((prev) => Array.from(new Set([...prev, ...filteredCourses.map((course) => course.id)])));
  };

  const clearSelectedCourses = () => {
    setSelectedCourses([]);
  };

  const normalizeText = (value) => String(value || '')
    .replace(/^\uFEFF/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  useEffect(() => {
    if (isOpen && autoEnroll) {
      fetchResources();
    }
  }, [isOpen, autoEnroll]);

  useEffect(() => {
    if (!selectedCompanyId && companies.length === 1) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    if (!selectedPartnerId && partners.length === 1) {
      setSelectedPartnerId(partners[0].id);
    }
  }, [partners, selectedPartnerId]);

  useEffect(() => {
    if (!isOpen) {
      setCompletionSummary(null);
      setCourseSearch('');
      setShowOnlySelectedCourses(false);
    }
  }, [isOpen]);

  // Carrega, sob demanda, o catálogo oficial (cargos/áreas) da empresa selecionada.
  const fetchCompanyCatalog = async (companyId) => {
    const id = String(companyId || '').trim();
    if (!id) return { cargos: [], areas: [] };
    try {
      const [{ data: cargosData }, { data: areasData }] = await Promise.all([
        supabase.from('company_cargos').select('id, name').eq('company_id', id).order('name'),
        supabase.from('company_areas').select('id, name').eq('company_id', id).order('name')
      ]);
      return { cargos: cargosData || [], areas: areasData || [] };
    } catch (error) {
      console.error('Error fetching company cargos/areas:', error);
      return { cargos: [], areas: [] };
    }
  };

  const filteredClasses = useMemo(() => {
    return availableClasses.filter(classItem => {
      const matchesCompany = !selectedCompanyId || classItem.company_id === selectedCompanyId;
      const matchesPartner = !classItem.partner_id || !selectedPartnerId || classItem.partner_id === selectedPartnerId;
      return matchesCompany && matchesPartner;
    });
  }, [availableClasses, selectedCompanyId, selectedPartnerId]);

  const filteredCourses = useMemo(() => {
    const search = normalizeText(courseSearch);
    return availableCourses.filter((course) => {
      const matchesSearch = !search || normalizeText(course?.name).includes(search);
      const matchesSelection = !showOnlySelectedCourses || selectedCourses.includes(course.id);
      return matchesSearch && matchesSelection;
    });
  }, [availableCourses, courseSearch, selectedCourses, showOnlySelectedCourses]);

  useEffect(() => {
    if (selectedClass && !filteredClasses.some(classItem => classItem.id === selectedClass)) {
      setSelectedClass('');
    }
  }, [filteredClasses, selectedClass]);

  const fetchResources = async () => {
    setLoadingResources(true);
    try {
      const { data: coursesData } = await supabase
        .from('courses')
        .select('id, name')
        .eq('status', 'Ativo')
        .order('name');
        
      const { data: classesData } = await supabase
        .from('classes')
        .select('id, name, company_id, partner_id')
        .in('status', ['Ativo', 'Programada'])
        .order('name');

      setAvailableCourses(coursesData || []);
      setAvailableClasses(classesData || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setLogs([]);
      setProgress({ current: 0, total: 0, success: 0, failed: 0 });
    }
  };

  const parseFile = async () => {
    try {
      if (file.name.endsWith('.csv')) {
        const csvRows = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
            const firstLine = lines[0] || '';
            const delimiter = firstLine.includes(';') ? ';' : ',';
            
            const parsedRows = lines.map(line => {
              return line.split(delimiter).map(cell => cell.trim());
            });
            resolve(parsedRows);
          };
          reader.onerror = (e) => reject(e);
          reader.readAsText(file);
        });

        return extractUsersFromRows(csvRows);
      }

      const rows = await readXlsxFile(file);
      return extractUsersFromRows(rows);
    } catch (error) {
      console.error('Error parsing file:', error);
      setLogs(prev => [...prev, { type: 'error', message: normalizeErrorMessage(error, 'arquivo') }]);
      return [];
    }
  };

  const mapHeaderIndexes = (headerRow) => {
    const indexes = Object.keys(FIELD_LABELS).reduce((acc, field) => {
      acc[field] = -1;
      return acc;
    }, {});

    headerRow.forEach((header, index) => {
      const normalizedHeader = normalizeText(header);
      const matchedField = Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.includes(normalizedHeader));
      if (matchedField) {
        indexes[matchedField[0]] = index;
      }
    });

    const missingHeaders = REQUIRED_FIELDS
      .filter(field => indexes[field] === -1)
      .map(field => FIELD_LABELS[field]);

    if (missingHeaders.length > 0) {
      throw new Error(`Arquivo inválido. Colunas obrigatórias ausentes: ${missingHeaders.join(', ')}. Baixe o template atualizado e tente novamente.`);
    }

    return indexes;
  };

  const getCellValue = (row, index) => {
    if (index === -1) return '';
    const value = row[index];
    return value === undefined || value === null ? '' : String(value).trim();
  };

  const extractUsersFromRows = (rows) => {
    if (!Array.isArray(rows) || rows.length <= 1) {
      throw new Error('O arquivo não possui linhas de usuários para importar.');
    }

    const headerIndexes = mapHeaderIndexes(rows[0] || []);
    const dataRows = rows
      .slice(1)
      .filter(row => Array.isArray(row) && row.some(cell => String(cell ?? '').trim() !== ''));

    if (dataRows.length === 0) {
      throw new Error('O arquivo não possui linhas de usuários para importar.');
    }

    return dataRows.map(row => ({
      name: getCellValue(row, headerIndexes.name),
      email: getCellValue(row, headerIndexes.email),
      company: getCellValue(row, headerIndexes.company),
      cpf: getCellValue(row, headerIndexes.cpf),
      partner: getCellValue(row, headerIndexes.partner),
      profile: getCellValue(row, headerIndexes.profile),
      whatsapp: getCellValue(row, headerIndexes.whatsapp),
      cargo: getCellValue(row, headerIndexes.cargo),
      area: getCellValue(row, headerIndexes.area)
    }));
  };

  const formatFieldList = (fields) => {
    return fields.map(field => FIELD_LABELS[field]).join(', ');
  };

  // Resolve um valor informado na planilha contra o catálogo oficial da empresa.
  // Quando a empresa possui catálogo, nomes divergentes são descartados (o nome
  // oficial prevalece). Sem catálogo cadastrado, o valor é mantido como informado.
  const resolveCatalogValue = (rawValue, lookup, hasCatalog) => {
    const value = String(rawValue || '').trim();
    if (!value) return { value: null, divergent: null };
    if (!hasCatalog) return { value, divergent: null };
    const official = lookup.get(normalizeText(value));
    if (official) return { value: official, divergent: null };
    return { value: null, divergent: value };
  };

  const normalizeErrorMessage = (error, context = 'processamento') => {
    if (!error) {
      return `Falha inesperada durante o ${context}.`;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof ProgressEvent !== 'undefined' && error instanceof ProgressEvent) {
      return 'Não foi possível ler o arquivo selecionado. Tente salvar novamente o CSV ou reenviar o arquivo.';
    }

    const message = String(error.message || error.error || '').trim();
    if (message) {
      if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        return `Falha de conexão durante o ${context}. Tente novamente.`;
      }

      if (message.includes('Tempo limite')) {
        return message;
      }

      return message;
    }

    return `Falha inesperada durante o ${context}.`;
  };

  const extractFunctionErrorMessage = async (error, fallbackMessage) => {
    if (!error) {
      return fallbackMessage;
    }

    if (error?.code === 'SESSION_EXPIRED' || error?.code === 'INVALID_TOKEN' || error?.code === 'INVALID_SESSION') {
      return 'Sessão expirada ou token inválido. Faça login novamente.';
    }

    if (error instanceof TypeError || error.message?.includes('fetch') || error.message?.includes('Failed to send')) {
      return 'Não foi possível conectar ao servidor de usuários. Tente novamente.';
    }

    try {
      const body = await error.context?.json?.();
      const code = body?.error ? String(body.error) : '';
      const msg = body?.message ? String(body.message) : '';
      const detailedMessage = (code && msg) ? `${code}: ${msg}` : (msg || code || body?.details);
      if (detailedMessage) {
        return String(detailedMessage);
      }
    } catch {}

    try {
      const rawText = await error.context?.text?.();
      if (rawText) {
        return String(rawText);
      }
    } catch {}

    return error.message || fallbackMessage;
  };

  const withTimeout = async (operation, message, timeoutMs = REQUEST_TIMEOUT_MS) => {
    let timeoutId;
    try {
      return await Promise.race([
        operation(),
        new Promise((_, reject) => {
          timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
        })
      ]);
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const wait = (ms) => new Promise(resolve => window.setTimeout(resolve, ms));

  const resolveCreatedUserId = async (email, fallbackData = null) => {
    const directId = fallbackData?.user_id || fallbackData?.user?.id || fallbackData?.id || null;
    if (directId) return directId;

    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) return null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const { data: profileRow, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (!profileError && profileRow?.id) {
        return profileRow.id;
      }

      if (attempt < 3) {
        await wait(250);
      }
    }

    return null;
  };

  const verifyStudentEnrollments = async (studentId, courseIds) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('offering:offering_id(course_id)')
      .eq('student_id', studentId);

    if (error) {
      throw new Error(`Não foi possível confirmar a matrícula no banco. ${error.message}`);
    }

    const enrolledCourseIds = new Set(
      (data || [])
        .map(item => item?.offering?.course_id)
        .filter(Boolean)
    );

    const missingCourseIds = (courseIds || []).filter(courseId => !enrolledCourseIds.has(courseId));
    return {
      isComplete: missingCourseIds.length === 0,
      missingCourseIds,
    };
  };

  const processBatch = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    setLogs([]);
    let successCount = 0;
    let failedCount = 0;
    let enrollmentWarningCount = 0;
    let createdOnlyCount = 0;
    let inviteWarningCount = 0;
    let catalogDivergenceCount = 0;
    let processedUsersTotal = 0;
    let selectedCourseDetails = [];
    let selectedClassDetails = null;
    const createdUsers = [];
    const createdWithoutEnrollmentUsers = [];
    const enrollmentCountsByCourseId = new Map();

    try {
      try {
        await getValidSessionAccessToken();
      } catch (sessionErr) {
        console.warn('[BulkCreateUserModal] sem sessão/tokens antes do processamento em massa');
        setLogs([{ type: 'error', message: 'Sessão expirada ou inválida. Faça login novamente.' }]);
        return;
      }
      if (!selectedCompanyId || !selectedPartnerId) {
        setLogs([{ type: 'error', message: 'Selecione a Empresa e o Parceiro SAS antes de processar o arquivo.' }]);
        return;
      }

      if (!inviteStartMode) {
        setLogs([{ type: 'error', message: 'Selecione se o convite deve iniciar imediatamente ou ser programado.' }]);
        return;
      }

      if (inviteStartMode === INVITE_START_MODES.scheduled) {
        const parsedInviteStart = parseInviteStartInput(inviteStartDate);
        if (!inviteStartDate) {
          setLogs([{ type: 'error', message: 'Informe a data e hora de início para o convite programado.' }]);
          return;
        }
        if (!parsedInviteStart) {
          setLogs([{ type: 'error', message: `Informe uma data e hora válidas no formato ${INVITE_START_PLACEHOLDER}.` }]);
          return;
        }
        if (new Date(parsedInviteStart.iso) < getSaoPauloNowDate()) {
          setLogs([{ type: 'error', message: 'Selecione uma data e hora futuras considerando o horário de São Paulo.' }]);
          return;
        }
      }

      const usersData = await parseFile();
      if (usersData.length === 0) {
        return;
      }

      const selectedCompany = companies.find(company => company.id === selectedCompanyId) || null;
      const selectedPartner = partners.find(partner => partner.id === selectedPartnerId) || null;
      selectedCourseDetails = availableCourses.filter(course => selectedCourses.includes(course.id));
      selectedClassDetails = filteredClasses.find(classItem => classItem.id === selectedClass) || null;

      if (!selectedCompany || !selectedPartner) {
        setLogs([{ type: 'error', message: 'A Empresa ou o Parceiro SAS selecionado não está mais disponível. Atualize a seleção e tente novamente.' }]);
        return;
      }

      const { cargos: catalogCargos, areas: catalogAreas } = await fetchCompanyCatalog(selectedCompany.id);
      const cargoNames = catalogCargos.map(item => item.name).filter(Boolean); 
      const areaNames = catalogAreas.map(item => item.name).filter(Boolean);   
      const cargoLookup = new Map(cargoNames.map(name => [normalizeText(name), name]));
      const areaLookup = new Map(areaNames.map(name => [normalizeText(name), name]));

      setProgress({ current: 0, total: usersData.length, success: 0, failed: 0 });
      processedUsersTotal = usersData.length;

      for (let i = 0; i < usersData.length; i++) {
        const userRow = usersData[i];
        const rowIndex = i + 2;

        try {
          const missingFields = REQUIRED_FIELDS.filter(field => !String(userRow[field] || '').trim());
          if (missingFields.length > 0) {
            throw new Error(`Linha ${rowIndex}: preencha os campos obrigatórios ${formatFieldList(missingFields)}.`);
          }

          const profileValue = String(userRow.profile || '').trim();
          const matchedRole = profileValue
            ? ALLOWED_ROLES.find(role => normalizeText(role) === normalizeText(profileValue))
            : 'Aluno';
          if (profileValue && !matchedRole) {
            throw new Error(`Linha ${rowIndex}: perfil "${userRow.profile}" inválido. Perfis aceitos: ${ALLOWED_ROLES.join(', ')}.`);
          }

          const resolvedCargo = resolveCatalogValue(userRow.cargo, cargoLookup, cargoNames.length > 0);
          const resolvedArea = resolveCatalogValue(userRow.area, areaLookup, areaNames.length > 0);
          if (resolvedCargo.divergent) {
            catalogDivergenceCount += 1;
            setLogs(prev => [...prev, { type: 'warning', message: `Linha ${rowIndex}: cargo "${resolvedCargo.divergent}" não corresponde aos cargos cadastrados da empresa e foi descartado.` }]);
          }
          if (resolvedArea.divergent) {
            catalogDivergenceCount += 1;
            setLogs(prev => [...prev, { type: 'warning', message: `Linha ${rowIndex}: área "${resolvedArea.divergent}" não corresponde às áreas cadastradas da empresa e foi descartada.` }]);
          }

          const payload = {
            action: 'create',
            payload: {
              email: userRow.email.toString().trim().toLowerCase(),
              full_name: userRow.name.toString().trim(),
              cpf: userRow.cpf.toString().replace(/\D/g, ''),
              role: matchedRole,
              status: 'Pendente',
              company_id: selectedCompany.id,
              sas_partner_id: selectedPartner.id,
              whatsapp: userRow.whatsapp ? userRow.whatsapp.toString().replace(/\D/g, '') : null,
              cargo: resolvedCargo.value,
              area: resolvedArea.value,
              password: 'Mudar@123',
              invite_start_mode: inviteStartMode,
              invite_start_at: inviteStartMode === INVITE_START_MODES.scheduled ? buildInviteStartAtIso(inviteStartDate) : null,
            }
          };

          const { data, error } = await withTimeout(
            () => invokeAdminManageUser(payload),
            `Linha ${rowIndex}: Tempo limite ao criar usuário.`
          );

          if (error) {
            const detailedError = await extractFunctionErrorMessage(error, 'Falha ao criar usuário.');
            throw new Error(`Linha ${rowIndex}: ${detailedError}`);
          }
          if (data?.error) throw new Error(`Erro Backend: ${data.error}`);

          const userId = await resolveCreatedUserId(payload.payload.email, data);

          if (autoEnroll && userId && selectedCourses.length > 0) {
            let enrollmentIssue = null;

            for (let attempt = 1; attempt <= 2; attempt += 1) {
              const { data: enrollData, error: enrollError } = await withTimeout(
                () => supabase.rpc('admin_enroll_student', {
                  p_student_id: userId,
                  p_course_ids: selectedCourses,
                  p_class_id: selectedClass || null,
                  p_duration_days: parseInt(validityDays),
                  p_start_date: inviteStartMode === INVITE_START_MODES.scheduled
                    ? buildInviteStartAtIso(inviteStartDate)
                    : null,
                }),
                `Linha ${rowIndex}: Tempo limite ao matricular usuário.`
              );

              const enrollFailCount = Number(enrollData?.fail_count || 0);
              const enrollSuccessCount = Number(enrollData?.success_count || 0);
              const enrollResults = Array.isArray(enrollData?.results) ? enrollData.results : [];
              const enrollMessages = enrollResults
                .filter(result => result?.status === 'error' && result?.message)
                .map(result => result.message);

              try {
                const verification = await verifyStudentEnrollments(userId, selectedCourses);
                if (!enrollError && enrollFailCount === 0 && enrollSuccessCount > 0 && verification.isComplete) {
                  selectedCourses.forEach(courseId => {
                    enrollmentCountsByCourseId.set(courseId, (enrollmentCountsByCourseId.get(courseId) || 0) + 1);
                  });
                  enrollmentIssue = null;
                  break;
                }

                enrollmentIssue = enrollError?.message
                  || enrollMessages[0]
                  || (!verification.isComplete ? 'A matrícula não foi localizada na relação de matrículas.' : null)
                  || 'A matrícula automática não foi concluída.';
              } catch (verificationError) {
                enrollmentIssue = verificationError.message || 'Não foi possível validar a matrícula automática.';
              }

              if (attempt < 2) {
                await wait(350);
              }
            }

            if (enrollmentIssue) {
              createdOnlyCount += 1;
              enrollmentWarningCount += 1;
              createdWithoutEnrollmentUsers.push({
                name: payload.payload.full_name,
                email: payload.payload.email,
              });
              throw new Error(`Linha ${rowIndex}: usuário criado, mas a matrícula falhou. ${enrollmentIssue}`);
            }
          }

          if (autoEnroll && !userId && selectedCourses.length > 0) {
            createdOnlyCount += 1;
            enrollmentWarningCount += 1;
            createdWithoutEnrollmentUsers.push({
              name: payload.payload.full_name,
              email: payload.payload.email,
            });
            throw new Error(`Linha ${rowIndex}: usuário criado, porém o retorno do cadastro não trouxe o ID e não foi possível confirmar o perfil para matrícula automática.`);
          }

          successCount += 1;
          createdUsers.push({
            name: payload.payload.full_name,
            email: payload.payload.email,
          });
          setProgress(prev => ({ ...prev, current: prev.current + 1, success: prev.success + 1 }));
        } catch (err) {
          console.error(err);
          failedCount += 1;
          setProgress(prev => ({ ...prev, current: prev.current + 1, failed: prev.failed + 1 }));
          setLogs(prev => [...prev, { type: 'error', message: normalizeErrorMessage(err, `cadastro da linha ${rowIndex}`) }]);
        }
      }
    } catch (err) {
      console.error(err);
      setLogs(prev => [...prev, { type: 'error', message: normalizeErrorMessage(err, 'processamento do arquivo em massa') }]);
    } finally {
      setIsProcessing(false);
    }

    const createdUsersCount = successCount + createdOnlyCount;
    const autoEnrollEnabled = autoEnroll && selectedCourses.length > 0;

    if (createdUsersCount > 0) {
      const courseSummaries = availableCourses
        .filter(course => selectedCourses.includes(course.id))
        .map(course => ({
          id: course.id,
          name: course.name,
          enrollmentCount: enrollmentCountsByCourseId.get(course.id) || 0,
        }));

      const totalEnrollmentsCreated = courseSummaries.reduce((acc, course) => acc + course.enrollmentCount, 0);

      setCompletionSummary({
        processedUsers: processedUsersTotal || successCount + failedCount,
        createdUsersCount,
        fullyEnrolledUsersCount: successCount,
        createdOnlyCount,
        failedCount,
        enrollmentWarningCount,
        inviteWarningCount,
        catalogDivergenceCount,
        autoEnrollEnabled,
        totalEnrollmentsCreated,
        selectedCompanyName: selectedCompanyId ? (companies.find(company => company.id === selectedCompanyId)?.name || '') : '',
        selectedPartnerName: selectedPartnerId ? (partners.find(partner => partner.id === selectedPartnerId)?.name || '') : '',
        selectedClassName: selectedClassDetails?.name || '',
        courseSummaries,
        createdUsers,
        createdWithoutEnrollmentUsers,
      });
    }

    if ((successCount > 0 || createdOnlyCount > 0) && onSuccess) {
      try {
        const totalEnrollmentsCreated = selectedCourseDetails.reduce((acc, course) => acc + (enrollmentCountsByCourseId.get(course.id) || 0), 0);
        const zeroEnrollmentsWithAutoEnroll = autoEnrollEnabled && createdUsersCount > 0 && totalEnrollmentsCreated === 0;
        const hasAutoEnrollSuccess = autoEnrollEnabled && enrollmentWarningCount === 0 && totalEnrollmentsCreated > 0;
        const successMessage = zeroEnrollmentsWithAutoEnroll
          ? `${createdUsersCount} usuário(s) criado(s), mas nenhuma matrícula foi efetivada.`
          : createdOnlyCount > 0
          ? `${createdOnlyCount} usuário(s) foi(foram) criado(s), mas a matrícula automática ficou pendente.`
          : inviteWarningCount > 0
            ? `${successCount} usuário(s) cadastrado(s)/matriculado(s), mas ${inviteWarningCount} solicitação(ões) de convite ao Supabase falharam.`
          : hasAutoEnrollSuccess
            ? `${successCount} usuário(s) cadastrado(s) e matriculado(s) com sucesso.`
            : `${successCount} usuário(s) cadastrado(s) com sucesso.`;

        setLogs(prev => [...prev, { type: (createdOnlyCount > 0 || zeroEnrollmentsWithAutoEnroll || inviteWarningCount > 0) ? 'warning' : 'success', message: successMessage }]);

        await onSuccess({
          successCount,
          failedCount,
          createdOnlyCount,
          enrollmentWarningCount,
          inviteWarningCount,
          autoEnrollEnabled,
          courseSummaries: selectedCourseDetails.map(course => ({
            id: course.id,
            name: course.name,
            enrollmentCount: enrollmentCountsByCourseId.get(course.id) || 0,
          })),
        });
      } catch (err) {
        console.error(err);
        setLogs(prev => [...prev, { type: 'warning', message: 'Cadastro concluído, mas houve falha ao atualizar a listagem.' }]);
      }
    }
  };

  const downloadTemplate = async () => {
    const { cargos, areas } = await fetchCompanyCatalog(selectedCompanyId);
    downloadUserTemplate({
      cargos: cargos.map(item => item.name).filter(Boolean),
      areas: areas.map(item => item.name).filter(Boolean)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <h3 className="text-xl font-bold text-white">Criar Usuários em Massa</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1">
          {/* File Upload Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-bold text-slate-400 uppercase">Arquivo de Importação</label>
              <button onClick={downloadTemplate} className="text-xs text-orange-500 hover:text-orange-400 font-bold underline">
                Baixar Template
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Selecione a empresa do lote antes de baixar o template para incluir as opções válidas de Cargo e Área. Valores divergentes no upload são descartados (valem os nomes oficiais da empresa).
            </p>
            
            <div className={`border-2 border-dashed rounded-lg p-8 text-center transition ${file ? 'border-teal-500/50 bg-teal-500/5' : 'border-slate-700 hover:border-slate-600 bg-slate-950'}`}>
              <input 
                type="file" 
                accept=".xlsx, .xls, .csv" 
                onChange={handleFileChange} 
                className="hidden" 
                id="file-upload"
                disabled={isProcessing}
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-3">
                {file ? (
                  <>
                    <FileSpreadsheet size={48} className="text-teal-500" />
                    <div>
                      <p className="text-white font-bold">{file.name}</p>
                      <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <span className="text-xs text-teal-400 font-bold mt-2">Clique para trocar</span>
                  </>
                ) : (
                  <>
                    <Upload size={48} className="text-slate-600" />
                    <div>
                      <p className="text-slate-300 font-medium">Clique para selecionar ou arraste o arquivo</p>
                      <p className="text-xs text-slate-500 mt-1">Suporta .xlsx, .xls, .csv</p>
                    </div>
                  </>
                )}
              </label>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-lg p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="bulk-company-select" className="text-xs font-bold text-slate-500 uppercase">Empresa do Lote</label>
                <select
                  id="bulk-company-select"
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                  disabled={isProcessing}
                >
                  <option value="">Selecione a empresa</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="bulk-partner-select" className="text-xs font-bold text-slate-500 uppercase">Parceiro SAS do Lote</label>
                <select
                  id="bulk-partner-select"
                  value={selectedPartnerId}
                  onChange={(e) => setSelectedPartnerId(e.target.value)}
                  className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                  disabled={isProcessing}
                >
                  <option value="">Selecione o parceiro SAS</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>{partner.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Empresa e Parceiro SAS serão aplicados a todos os usuários deste lote. As colunas correspondentes no arquivo são opcionais. O CPF tambem pode ficar em branco.
            </p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-lg p-5 mb-6">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-bold text-slate-400 uppercase">Início do Convite</p>
                <p className="text-xs text-slate-500 mt-1">Escolha quando o sistema deve disparar os convites deste lote.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (isProcessing) return;
                    setInviteStartMode(INVITE_START_MODES.immediate);
                    setInviteStartDate('');
                  }}
                  className={`rounded-lg border px-4 py-3 text-left transition ${
                    inviteStartMode === INVITE_START_MODES.immediate
                      ? 'border-orange-500 bg-orange-500/10 text-white'
                      : 'border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-700'
                  }`}
                  disabled={isProcessing}
                >
                  <span className="block text-sm font-bold">Início Imediato</span>
                  <span className="block text-xs text-slate-400 mt-1">Mantém o fluxo atual e envia os convites assim que cada usuário for criado.</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (isProcessing) return;
                    setInviteStartMode(INVITE_START_MODES.scheduled);
                  }}
                  className={`rounded-lg border px-4 py-3 text-left transition ${
                    inviteStartMode === INVITE_START_MODES.scheduled
                      ? 'border-orange-500 bg-orange-500/10 text-white'
                      : 'border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-700'
                  }`}
                  disabled={isProcessing}
                >
                  <span className="block text-sm font-bold">Programar Início</span>
                  <span className="block text-xs text-slate-400 mt-1">Cria os usuários agora e agenda o envio dos convites para a data escolhida.</span>
                </button>
              </div>

              {inviteStartMode === INVITE_START_MODES.scheduled && (
                <div className="max-w-sm flex flex-col gap-1.5 animate-fade-in">
                  <ScheduledDateTimePicker
                    id="bulk-invite-start-date"
                    label="Data e Hora de Início"
                    value={inviteStartDate}
                    onChange={setInviteStartDate}
                    disabled={isProcessing}
                    helperText={`Formato: ${INVITE_START_PLACEHOLDER}. Os convites deste lote serao disparados automaticamente na data e hora informadas no horario de Sao Paulo.`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Auto Enroll Section */}
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-6 rounded-full p-1 cursor-pointer transition-colors ${autoEnroll ? 'bg-orange-500' : 'bg-slate-700'}`} onClick={() => !isProcessing && setAutoEnroll(!autoEnroll)}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${autoEnroll ? 'translate-x-4' : ''}`} />
                </div>
                <div>
                  <h4 className="font-bold text-white">Matrícula Automática</h4>
                  <p className="text-xs text-slate-500">Matricular usuários criados em cursos/turmas</p>
                </div>
              </div>
            </div>

            {autoEnroll && (
              <div className="flex flex-col gap-4 pl-2 border-l-2 border-slate-800 ml-4 mt-2 animate-fade-in">
                {loadingResources ? (
                  <div className="py-4 text-center text-slate-500 flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Carregando cursos e turmas...
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.8fr)_minmax(280px,1fr)] gap-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-xs font-bold text-slate-500 uppercase">Cursos</label>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            {selectedCourses.length} selecionado(s)
                          </span>
                        </div>
                        <input
                          type="text"
                          value={courseSearch}
                          onChange={(e) => setCourseSearch(e.target.value)}
                          placeholder="Buscar curso..."
                          className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white text-sm focus:border-orange-500 outline-none placeholder:text-slate-500"
                          disabled={isProcessing}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={selectAllVisibleCourses}
                            disabled={isProcessing || filteredCourses.length === 0}
                            className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Selecionar todos
                          </button>
                          <button
                            type="button"
                            onClick={clearSelectedCourses}
                            disabled={isProcessing || selectedCourses.length === 0}
                            className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Limpar seleção
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowOnlySelectedCourses((prev) => !prev)}
                            disabled={isProcessing || selectedCourses.length === 0}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                              showOnlySelectedCourses
                                ? 'border-orange-500 bg-orange-500/10 text-orange-300'
                                : 'border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
                            }`}
                          >
                            Mostrar apenas selecionados
                          </button>
                        </div>
                        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 max-h-72 overflow-y-auto space-y-2">
                          {availableCourses.length === 0 ? (
                            <p className="text-sm text-slate-500">Nenhum curso ativo disponível.</p>
                          ) : filteredCourses.length === 0 ? (
                            <p className="text-sm text-slate-500">Nenhum curso encontrado para a busca informada.</p>
                          ) : (
                            filteredCourses.map((course) => {
                              const isSelected = selectedCourses.includes(course.id);
                              return (
                                <label
                                  key={course.id}
                                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                                    isSelected
                                      ? 'border-orange-500 bg-orange-500/10'
                                      : 'border-slate-700 bg-slate-950/60 hover:border-slate-500'
                                  } ${isProcessing ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-950 text-orange-500 focus:ring-orange-500"
                                    checked={isSelected}
                                    onChange={() => toggleCourseSelection(course.id)}
                                    disabled={isProcessing}
                                  />
                                  <span className="text-sm leading-5 text-white break-words">{course.name}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500">Marque todos os cursos em que os usuários do lote devem ser matriculados.</p>
                      </div>

                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="bulk-validity-days" className="text-xs font-bold text-slate-500 uppercase">Validade (Dias)</label>
                          <input 
                            id="bulk-validity-days"
                            type="number" 
                            min="1"
                            value={validityDays}
                            onChange={(e) => setValidityDays(e.target.value)}
                            className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                            disabled={isProcessing}
                          />
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="bulk-class-select" className="text-xs font-bold text-slate-500 uppercase">Vincular Turma (Opcional)</label>
                          <select 
                            id="bulk-class-select"
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                            disabled={isProcessing}
                          >
                            <option value="">Nenhuma</option>
                            {filteredClasses.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          {selectedCompanyId && selectedPartnerId && filteredClasses.length === 0 && (
                            <p className="text-[10px] text-amber-400">Nenhuma turma ativa compatível com a empresa e o parceiro SAS do lote.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Progress & Logs */}
          {(isProcessing || progress.total > 0 || logs.length > 0) && (
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
              <div className="flex justify-between text-xs font-bold text-slate-400 mb-2">
                <span>Progresso: {progress.current} / {progress.total}</span>
                <span className="text-teal-500">{progress.success} Sucessos</span>
                <span className="text-rose-500">{progress.failed} Falhas</span>
              </div>
              <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden mb-4">
                <div 
                  className="bg-orange-500 h-full transition-all duration-300" 
                  style={{ width: `${(progress.current / (progress.total || 1)) * 100}%` }}
                />
              </div>
              
              <div className="max-h-32 overflow-y-auto text-xs space-y-1 custom-scrollbar">
                {logs.map((log, i) => (
                  <div key={i} className={`${log.type === 'error' ? 'text-rose-400' : log.type === 'warning' ? 'text-amber-400' : log.type === 'success' ? 'text-teal-400' : 'text-slate-400'}`}>
                    {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-900">
          <button 
            onClick={onClose} 
            className="px-5 py-2.5 rounded-lg font-bold text-slate-400 hover:bg-white/5 transition"
            disabled={isProcessing}
          >
            Cancelar
          </button>
          <button 
            onClick={processBatch}
            disabled={!file || !selectedCompanyId || !selectedPartnerId || !inviteStartMode || isProcessing || (inviteStartMode === INVITE_START_MODES.scheduled && !inviteStartDate) || (autoEnroll && selectedCourses.length === 0)}
            className="bg-orange-600 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg hover:bg-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isProcessing ? <><Loader2 size={18} className="animate-spin" /> Processando...</> : 'Processar Arquivo'}
          </button>
        </div>

        {completionSummary && (
          <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
              <div className="p-6 border-b border-slate-800 flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-xl font-bold text-white">Resultado do Processamento</h4>
                  <p className="text-sm text-slate-400 mt-1">Resumo efetivo do que foi criado no lote.</p>
                </div>
                {completionSummary.autoEnrollEnabled && completionSummary.createdUsersCount > 0 && completionSummary.totalEnrollmentsCreated === 0 ? (
                  <div className="w-10 h-10 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center">
                    <AlertCircle size={20} />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-teal-500/15 text-teal-400 flex items-center justify-center">
                    <Check size={20} />
                  </div>
                )}
              </div>

              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {completionSummary.autoEnrollEnabled && completionSummary.createdUsersCount > 0 && completionSummary.totalEnrollmentsCreated === 0 && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
                    <p className="text-sm font-bold text-amber-300">Atenção: nenhuma matrícula foi criada neste lote.</p>
                    <p className="text-xs text-amber-200/80 mt-1">Os usuários foram cadastrados, mas a matrícula automática não foi efetivada. Revise ofertas ativas e permissões da RPC.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="text-xs uppercase font-bold text-slate-500">Usuários criados</p>
                    <p className="text-2xl font-bold text-white mt-1">{completionSummary.createdUsersCount}</p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="text-xs uppercase font-bold text-slate-500">Matrículas criadas</p>
                    <p className="text-2xl font-bold text-white mt-1">{completionSummary.totalEnrollmentsCreated}</p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="text-xs uppercase font-bold text-slate-500">Criados sem matrícula concluída</p>
                    <p className="text-2xl font-bold text-amber-400 mt-1">{completionSummary.createdOnlyCount}</p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="text-xs uppercase font-bold text-slate-500">Falhas de cadastro</p>
                    <p className="text-2xl font-bold text-rose-400 mt-1">{Math.max(completionSummary.failedCount - completionSummary.createdOnlyCount, 0)}</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 space-y-2">
                  <p className="text-xs uppercase font-bold text-slate-500">Resumo do lote</p>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p><span className="text-slate-500">Empresa:</span> {completionSummary.selectedCompanyName || '-'}</p>
                    <p><span className="text-slate-500">Parceiro SAS:</span> {completionSummary.selectedPartnerName || '-'}</p>
                    <p><span className="text-slate-500">Turma:</span> {completionSummary.selectedClassName || 'Nenhuma turma vinculada'}</p>
                    <p><span className="text-slate-500">Usuários processados:</span> {completionSummary.processedUsers}</p>
                    {Number(completionSummary.inviteWarningCount || 0) > 0 && (
                      <p><span className="text-slate-500">Solicitações de convite com falha:</span> <span className="text-amber-300 font-bold">{completionSummary.inviteWarningCount}</span></p>
                    )}
                    {Number(completionSummary.catalogDivergenceCount || 0) > 0 && (
                      <p><span className="text-slate-500">Cargos/áreas divergentes descartados:</span> <span className="text-amber-300 font-bold">{completionSummary.catalogDivergenceCount}</span></p>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                  <p className="text-xs uppercase font-bold text-slate-500 mb-3">Matrículas por curso</p>
                  {completionSummary.autoEnrollEnabled ? (
                    completionSummary.courseSummaries.length > 0 ? (
                      <div className="space-y-2">
                        {completionSummary.courseSummaries.map(course => (
                          <div key={course.id} className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                            <span className="text-sm text-white">{course.name}</span>
                            <span className="text-sm font-bold text-teal-400">{course.enrollmentCount} matrícula(s)</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">Nenhum curso foi selecionado para matrícula automática.</p>
                    )
                  ) : (
                    <p className="text-sm text-slate-400">A matrícula automática não foi usada neste lote.</p>
                  )}
                </div>

                <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                  <p className="text-xs uppercase font-bold text-slate-500 mb-3">Usuários criados efetivamente</p>
                  {completionSummary.createdUsersCount > 0 ? (
                    <div className="space-y-2">
                      {[...completionSummary.createdUsers, ...completionSummary.createdWithoutEnrollmentUsers].map(user => (
                        <div key={`${user.email}-${user.name}`} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                          <p className="text-sm font-semibold text-white">{user.name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">Nenhum usuário foi criado neste processamento.</p>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-slate-800 bg-slate-900 flex justify-end">
                <button
                  onClick={onClose}
                  className="bg-orange-600 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg hover:bg-orange-500 transition"
                >
                  Fechar Resumo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkCreateUserModal;
